import { ICommonSettings } from "@kuika/kuika-cl-model";
import { PureComponent, ReactNode } from "react";
import { ICommonConfigDeleteFunctionParams, ICommonConfigSaveFunctionParams } from "../app-settings-config-container";
import { ConfigSettingSelect } from "../config-setting-select";
import { ConfigAzureOpenAISettings } from "./app-settings-config-azure-openai";
import { ConfigGeminiSettings } from "./app-settings-config-gemini";
import { ConfigOpenAISettings } from "./app-settings-config-open-ai";
import { ConfigSerperToolSettings } from "./app-settings-config-serper-tool";

export interface IConfigAIInputState {
  showOpenAIModal: boolean;
  showAzureOpenAIModal: boolean;
  showGeminiModal: boolean;
  showSerperToolModal: boolean;
  editingOpenAIId: string;
  editingAzureOpenAIId: string;
  editingGeminiId: string;
  editingSerperToolId: string;
}

export interface IConfigAIInputProps {
  parentConfigOpenAIValue?: string | null;
  parentConfigAzureOpenAIValue?: string | null;
  parentConfigGeminiValue?: string | null;
  parentConfigSerperToolValue?: string | null;
  parentConfigParamValue?: string | null;
  commonSettings: ICommonSettings;
  getSectionDescription: (contentKey: string) => ReactNode;
  handleUpdateCustomFormInput?: (formItemName: string[], value: string | null) => void;
  handleCreateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleUpdateCommonConfig: (params: ICommonConfigSaveFunctionParams) => void;
  handleDeleteCommonConfig: (params: ICommonConfigDeleteFunctionParams) => void;
}

/** The four model providers an AI function can call. One picker each. */
export class ConfigAIInput extends PureComponent<IConfigAIInputProps, IConfigAIInputState> {
  openAIFormInputName: string[] = ["apiConfig", "openAISettingsId"];

  azureOpenAIFormInputName: string[] = ["apiConfig", "azureOpenAISettingsId"];

  geminiFormInputName: string[] = ["apiConfig", "geminiSettingsId"];

  serperToolFormInputName: string[] = ["apiConfig", "serperToolSettingsId"];

  constructor(props: IConfigAIInputProps) {
    super(props);
    this.state = {
      showOpenAIModal: false,
      editingOpenAIId: "",
      showAzureOpenAIModal: false,
      editingAzureOpenAIId: "",
      showGeminiModal: false,
      editingGeminiId: "",
      showSerperToolModal: false,
      editingSerperToolId: ""
    };
  }

  openOpenAIModal = (): void => {
    this.setState({ editingOpenAIId: "", showOpenAIModal: true });
  };

  closeOpenAIModal = (): void => {
    this.setState({ showOpenAIModal: false });
  };

  setEditingOpenAI = (id: string): void => {
    this.setState({ editingOpenAIId: id, showOpenAIModal: true });
  };

  openAzureOpenAIModal = (): void => {
    this.setState({ editingAzureOpenAIId: "", showAzureOpenAIModal: true });
  };

  closeAzureOpenAIModal = (): void => {
    this.setState({ showAzureOpenAIModal: false });
  };

  setEditingAzureOpenAI = (id: string): void => {
    this.setState({ editingAzureOpenAIId: id, showAzureOpenAIModal: true });
  };

  openGeminiModal = (): void => {
    this.setState({ editingGeminiId: "", showGeminiModal: true });
  };

  closeGeminiModal = (): void => {
    this.setState({ showGeminiModal: false });
  };

  setEditingGemini = (id: string): void => {
    this.setState({ editingGeminiId: id, showGeminiModal: true });
  };

  openSerperToolModal = (): void => {
    this.setState({ editingSerperToolId: "", showSerperToolModal: true });
  };

  closeSerperToolModal = (): void => {
    this.setState({ showSerperToolModal: false });
  };

  setEditingSerperTool = (id: string): void => {
    this.setState({ editingSerperToolId: id, showSerperToolModal: true });
  };

  restore = (formInputName: string[], parentValue: string | null | undefined): void => {
    this.props.handleUpdateCustomFormInput?.(formInputName, parentValue ?? null);
  };

  render(): ReactNode {
    const {
      parentConfigOpenAIValue,
      parentConfigAzureOpenAIValue,
      parentConfigGeminiValue,
      parentConfigSerperToolValue,
      commonSettings,
      getSectionDescription,
      handleCreateCommonConfig,
      handleUpdateCommonConfig,
      handleDeleteCommonConfig
    } = this.props;

    return (
      <>
        <ConfigOpenAISettings
          formInputName={this.openAIFormInputName}
          closeModal={this.closeOpenAIModal}
          showModal={this.state.showOpenAIModal}
          editingOpenAIId={this.state.editingOpenAIId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigAzureOpenAISettings
          formInputName={this.azureOpenAIFormInputName}
          closeModal={this.closeAzureOpenAIModal}
          showModal={this.state.showAzureOpenAIModal}
          editingAzureOpenAIId={this.state.editingAzureOpenAIId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigGeminiSettings
          formInputName={this.geminiFormInputName}
          closeModal={this.closeGeminiModal}
          showModal={this.state.showGeminiModal}
          editingGeminiId={this.state.editingGeminiId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigSerperToolSettings
          formInputName={this.serperToolFormInputName}
          closeModal={this.closeSerperToolModal}
          showModal={this.state.showSerperToolModal}
          editingSerperToolId={this.state.editingSerperToolId}
          commonSettings={commonSettings}
          getSectionDescription={getSectionDescription}
          handleCreateCommonConfig={handleCreateCommonConfig}
          handleUpdateCommonConfig={handleUpdateCommonConfig}
          handleDeleteCommonConfig={handleDeleteCommonConfig}
        />

        <ConfigSettingSelect
          label="OpenAI"
          formInputName={this.openAIFormInputName}
          options={commonSettings.openAISettings ?? []}
          allowClear
          tooltipContentKey="appsettings_ai_openai"
          onAddNew={this.openOpenAIModal}
          onEdit={this.setEditingOpenAI}
          parentConfigValue={parentConfigOpenAIValue}
          onRestore={() => this.restore(this.openAIFormInputName, parentConfigOpenAIValue)}
        />

        <ConfigSettingSelect
          label="Azure OpenAI"
          formInputName={this.azureOpenAIFormInputName}
          options={commonSettings.azureOpenAISettings ?? []}
          allowClear
          tooltipContentKey="appsettings_ai_azureopenai"
          onAddNew={this.openAzureOpenAIModal}
          onEdit={this.setEditingAzureOpenAI}
          parentConfigValue={parentConfigAzureOpenAIValue}
          onRestore={() => this.restore(this.azureOpenAIFormInputName, parentConfigAzureOpenAIValue)}
        />

        <ConfigSettingSelect
          label="Gemini"
          formInputName={this.geminiFormInputName}
          options={commonSettings.geminiSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_ai_gemini"
          onAddNew={this.openGeminiModal}
          onEdit={this.setEditingGemini}
          parentConfigValue={parentConfigGeminiValue}
          onRestore={() => this.restore(this.geminiFormInputName, parentConfigGeminiValue)}
        />

        <ConfigSettingSelect
          label="Serper Tool"
          formInputName={this.serperToolFormInputName}
          options={commonSettings.serperToolSettings ?? []}
          allowClear
          tooltipContentKey="appsettings_serpertool_description"
          onAddNew={this.openSerperToolModal}
          onEdit={this.setEditingSerperTool}
          parentConfigValue={parentConfigSerperToolValue}
          onRestore={() => this.restore(this.serperToolFormInputName, parentConfigSerperToolValue)}
        />
      </>
    );
  }
}
